import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { exchangeImpersonationCode } from '../../../api/impersonation'
import { STORAGE_KEYS } from '../../../utils/constants'
import { useAuth } from '../../../contexts/AuthContext'

/**
 * Consumes ?code= from Super Master one-click login and establishes a merchant session.
 */
export default function ImpersonatePage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { adoptStoredSession } = useAuth()
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    const code = searchParams.get('code')

    // Strip code from URL/history immediately so it cannot leak via Referer/logs
    if (code && typeof window !== 'undefined') {
      const clean = `${window.location.pathname}${window.location.hash || ''}`
      window.history.replaceState({}, document.title, clean)
    }

    async function run() {
      if (!code) {
        setError('Missing impersonation code.')
        return
      }
      try {
        const data = await exchangeImpersonationCode(code)
        if (cancelled) return
        if (!data?.token) {
          setError('Exchange failed: no token returned.')
          return
        }

        localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, data.token)
        if (data.merchantId != null) {
          localStorage.setItem(STORAGE_KEYS.MERCHANT_ID, String(data.merchantId))
        }
        if (data.masterId != null) {
          localStorage.setItem('merchantMasterId', String(data.masterId))
        }
        if (data.branding?.slug) {
          localStorage.setItem('brandSlug', data.branding.slug)
        }
        sessionStorage.setItem('impersonationActive', '1')

        if (data.branding) {
          window.dispatchEvent(
            new CustomEvent('blacktape:branding-apply', { detail: data.branding })
          )
        }
        window.dispatchEvent(new Event('blacktape:branding-refresh'))

        adoptStoredSession()
        toast.success('Impersonation login successful')
        navigate('/dashboard', { replace: true })
      } catch (e) {
        if (cancelled) return
        const msg =
          e.response?.data?.message || e.message || 'Impersonation exchange failed'
        setError(msg)
        toast.error(msg)
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [searchParams, navigate, adoptStoredSession])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white border border-gray-200 rounded-lg p-8 text-center shadow-sm">
        {error ? (
          <>
            <h1 className="text-lg font-semibold text-red-700">Impersonation failed</h1>
            <p className="mt-2 text-sm text-gray-600">{error}</p>
            <button
              type="button"
              className="mt-6 text-sm text-blue-700 hover:underline"
              onClick={() => navigate('/login', { replace: true })}
            >
              Go to login
            </button>
          </>
        ) : (
          <>
            <h1 className="text-lg font-semibold text-gray-900">Signing you in…</h1>
            <p className="mt-2 text-sm text-gray-600">
              Exchanging one-time code. This link expires quickly and can only be used once.
            </p>
          </>
        )}
      </div>
    </div>
  )
}
